import { motion, AnimatePresence } from 'framer-motion'
import { X, CheckCircle, AlertCircle, Info, PartyPopper } from 'lucide-react'
import { useToast, type ToastType } from '@/context/ToastContext'
import { cn } from '@/lib/utils'

const toastIcons: Record<ToastType, React.ElementType> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  celebration: PartyPopper,
}

const toastStyles: Record<ToastType, string> = {
  success: 'bg-[var(--color-kids-success)] text-white',
  error: 'bg-[var(--color-kids-danger)] text-white',
  info: 'bg-[var(--color-kids-primary)] text-white',
  celebration: 'bg-gradient-to-r from-[var(--color-kids-secondary)] to-[var(--color-kids-accent)] text-white',
}

export function ToastManager() {
  const { toasts, removeToast } = useToast()

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const Icon = toastIcons[toast.type]

          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className={cn(
                'flex items-center gap-3 px-5 py-4 rounded-[var(--radius-kid)] shadow-[var(--shadow-kid-lg)] min-w-[280px] max-w-[400px]',
                toastStyles[toast.type]
              )}
            >
              <Icon className="w-6 h-6 flex-shrink-0" />
              <span className="flex-1 text-[var(--font-size-kid-base)] font-semibold">
                {toast.message}
              </span>
              <button
                onClick={() => removeToast(toast.id)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss notification"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Celebration confetti effect */}
              {toast.type === 'celebration' && (
                <motion.div
                  className="absolute inset-0 pointer-events-none overflow-hidden rounded-[var(--radius-kid)]"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 2 }}
                >
                  {[...Array(12)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5],
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                      }}
                      initial={{ scale: 0, rotate: 0 }}
                      animate={{
                        scale: [0, 1, 0],
                        rotate: [0, 180, 360],
                        y: [0, -20, 20],
                      }}
                      transition={{
                        duration: 1.5,
                        delay: i * 0.1,
                        repeat: 1,
                      }}
                    />
                  ))}
                </motion.div>
              )}
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
